import AddIcon from '@mui/icons-material/Add';
import { Button, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANGABEN_ZUM_VORSTEUERABZUG_KREATIV_ANTRAG,
	ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import PositionLiegtNichtVor from 'src/pages/private/online-antrag/antrag-components/PositionLiegtNichtVor';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import PruefAssistenz from '../../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../../application-heading/ApplicationHeading';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from '../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const SachkostenAntragKreativwirtschaft: FC<{ kostentraeger: string; unternehmenId?: number; hasComment?: boolean }> = ({
	kostentraeger,
	unternehmenId,
	hasComment,
}) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleAddSachkosten = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			kostentraeger,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Sachkosten,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(application, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteSachKosten = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Sachkosten', err);
		}
	};

	const showHinweisVorsteuerabzugWiderspricht = useMemo(() => {
		const orgaVorsteuerabzug = get(
			application,
			P.Antragstellende_Organisation.schema.attributes.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug.path,
		);
		const sachkostenVorsteuerabzug = get(
			application,
			P.Projektbeschreibung_Kreativwirtschaft.schema.attributes.Projektbeschreibung_Kreativ_Angaben_Zum_Vorsteuerabzug.path,
		);

		// Wenn einer der Werte leer/nicht gesetzt ist, return false
		if (!orgaVorsteuerabzug || !sachkostenVorsteuerabzug) {
			return false;
		}

		// Wenn beide übereinstimmen, return false
		if (
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.Vorsteuerabzugsberechtigt &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_KREATIV_ANTRAG.Vorsteuerabzugsberechtigt) ||
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.keineVorsteuerabzugsberechtigung &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_KREATIV_ANTRAG.keineVorsteuerabzugsberechtigung)
		) {
			return false;
		}

		// Nur wenn beide gesetzt sind UND abweichen, return true
		return true;
	}, [application]);

	// Fetch KostenKategories
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter(
					{
						Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
						Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Sachkosten,
						Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
						Allgemein_Aktiv: { $eq: true },
					},
					APIS.Antrag,
				);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data'))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategorie ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Sachkosten' description={PLATZHALTER_TEXT} />
			<PositionLiegtNichtVor kostenPosition={KOSTEN_POSITION.Ausgabe} kostenArt={KOSTEN_ARTEN.Sachkosten} updatedReadOnly={updatedReadOnly} />

			<InhaltTrenner marginTop='1rem' />
			<InhaltWrapper marginTop='1rem'>
				<Typography fontWeight={'bold'}>Zu den förderfähigen Sachausgaben gehören beispielsweise:</Typography>
				<ul>
					<li>Ausgaben für an Dritte vergebene Aufträge</li>
					<li>Ausgaben für geringwertige Wirtschaftsgüter </li>
					<li>Miete oder Leasing von Ausstattungsgegenständen </li>
					<li>Druckkosten von Printmedien/Werbung </li>
					<li>Miete für Räume, die für Beratungen genutzt werden </li>
				</ul>
				<Typography>
					Zuwendungsfähige Sachausgaben sind Ausgaben, die in unmittelbarem Zusammenhang mit dem geförderten Vorhaben stehen und nicht bereits
					über die Gemeinkosten abgedeckt sind.{' '}
				</Typography>
			</InhaltWrapper>
			<InhaltTrenner marginTop='1rem' />
			<InhaltWrapper>
				<Typography fontWeight={'bold'}>Erklärung zum Vorsteuerabzug</Typography>
				<Typography>
					<strong>Bitte beachten Sie:</strong> Bei Vorsteuerabzugsberechtigung sind die Sachkosten als <u>Netto-Beträge</u> anzusetzen. Um die
					von Ihnen angegebenen Kosten korrekt zu bewerten, geben Sie bitte hier an, was auf die von Ihnen gemachten Angaben und Ihren Status
					zutrifft:
				</Typography>

				{showHinweisVorsteuerabzugWiderspricht && (
					<MUIAlert
						infoType={'warning'}
						message={
							'Die hier abgegebene Erklärung zum Vorsteuerabzug widerspricht den von Ihnen gemachten Angaben im Organisationsprofil. Bitte überprüfen Sie Ihre Angaben und korrigieren Sie diese an der betreffenden Stelle.'
						}
					/>
				)}
				<CreateForm
					formStruc={vorsteuerStruc()}
					formValues={application}
					updateFormValuesFunc={setApplicationProperty}
					readOnly={updatedReadOnly}
				/>
				<Typography sx={{ mt: 2 }} fontWeight={'bold'}>
					Sollten sich während der Projektlaufzeit Änderungen an der Vorsteuerabzugsberechtigung ergeben, so sind diese der Bewilligungsbehörde
					unverzüglich mitzuteilen.
				</Typography>
			</InhaltWrapper>
			<InhaltTrenner marginTop='1rem' marginBottom='1rem' />

			{/* eslint-disable-next-line array-callback-return */}
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Sachkosten ${get(ele, KP_P.Allgemein_Bezeichnung.path, '') ? '| ' + get(ele, KP_P.Allgemein_Bezeichnung.path) : ''}`}
							onDelete={() => handleDeleteSachKosten(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapKosten(formStrucPruefAssistenz(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>
							<Grid2 spacing={6} container>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(formStrucSpalte1(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(formStrucSpalte2(), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
							</Grid2>
							<CreateForm
								formStruc={mapKosten(formStrucErlaeterung(), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
								size='small'
							/>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} onClick={handleAddSachkosten} disabled={updatedReadOnly}>
					Sachkosten hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default SachkostenAntragKreativwirtschaft;

const vorsteuerStruc = () => {
	const struc = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Kreativwirtschaft.schema.attributes.Projektbeschreibung_Kreativ_Angaben_Zum_Vorsteuerabzug,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: '',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucSpalte1 = (kostenKategories?: any[]) => {
	const kategorieOptions = kostenKategories || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'enumeration',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
				P.Foerderantrag_Kostenpositions.schema.attributes.Sachausgaben_Kreativwirtschaft.schema.attributes.Art_Der_Leistung,
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucSpalte2 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Sachausgaben_Kreativwirtschaft.schema.attributes.Einzelkosten,
					format: 'currency',
				},
				P.Foerderantrag_Kostenpositions.schema.attributes.Sachausgaben_Kreativwirtschaft.schema.attributes.Anzahl,
				{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme, format: 'currency', disabled: true },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucErlaeterung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Erlaeuterungen,
					tooltip: 'Erläuterung (z.B. zur Höhe oder Notwendigkeit der Ausgaben u./o. zur Kalkulationsbasis, sofern nicht eindeutig ersichtlich)',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];

	return struc;
};

const mapKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};
export const getFormStrucSachkostenAntragKreativwirtschaft = (application: any, unternehmenId: number) => {
	let result: FormStrucItem[] = [];

	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);

			const formStrucSpalte1WithIndex = mapKosten(formStrucSpalte1(), i);
			const formStrucSpalte2WithIndex = mapKosten(formStrucSpalte2(), i);

			if (isHinweisFieldRequired) {
				result = concat(mapKosten(formStrucPruefAssistenz(), i), result, formStrucSpalte1WithIndex, formStrucSpalte2WithIndex, vorsteuerStruc());
			} else {
				result = concat(result, formStrucSpalte1WithIndex, formStrucSpalte2WithIndex, vorsteuerStruc());
			}
		}
	});

	return result;
};
